import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ArticleInterface } from '@modules/article/interfaces/article.interface';
import { Adapter } from '@modules/content-api/adapter/adapter';
import { ContactPersonAdapter, ContactPersonResponse } from '@modules/content-api/adapter/contact.adapter';
import {
  MediaAdapter,
  MediaAudioResponse,
  MediaDocumentResponse,
  MediaImageResponse,
  MediaVideoResponse,
} from '@modules/content-api/adapter/media.adapter';
import { PreMediaDetailImageStyle } from '@modules/content-api/domain/image-styles/pre-media-detail-image.style';
import { PreMediaListImageStyle } from '@modules/content-api/domain/image-styles/pre-media-list-image.style';

import { PreMediaImageHelper } from './pre-media-image.helper';

@Injectable({
  providedIn: 'root',
})
export class PreMediaArticleAdapter implements Adapter<ArticleInterface> {
  private contactPersonAdapter = inject(ContactPersonAdapter);
  private mediaAdapter = inject(MediaAdapter);
  private translateService = inject(TranslateService);
  private preMediaImageHelper = inject(PreMediaImageHelper);

  public parse(data: any): ArticleInterface {
    const context = { id: data.id, type: 'articles' };
    const slug =
      data.slug?.find(({ lang }: { lang: string }) => lang === this.translateService.currentLang()?.split('-')[0])
        ?.value ||
      (data.slugs && data.slugs[0].value) ||
      '';
    return {
      id: data.id,
      marsOriginId: data.mars_origin_id,
      type: data.type,
      title: data.title,
      slug: slug,
      slugs: data.slug,
      langs: data.langs,
      content: data.content?.value || '',
      // header images
      headerImage: data.header_image
        ? this.preMediaImageHelper.parseImageWithWatermark(data.header_image, PreMediaListImageStyle, context)
        : null,
      fuelLabel: data.fuel_label_text?.processed,
      displayDate: data.display_date,
      location: data.location,

      //gallery images
      images: (data.images || []).map((image: MediaImageResponse) => {
        return this.preMediaImageHelper.parseImageWithWatermark(image, PreMediaDetailImageStyle, context, slug);
      }),
      documents: (data.documents || []).map((document: MediaDocumentResponse) =>
        this.mediaAdapter.parseDocument(document, context)
      ),
      technicalDatas: (data.technical_data || []).map((technicalDatas: MediaDocumentResponse) =>
        this.mediaAdapter.parseDocument(technicalDatas, context)
      ),
      audios: (data.audios || []).map((audio: MediaAudioResponse) =>
        this.mediaAdapter.parseAudio(audio, context, slug)
      ),
      videos: (data.videos || []).map((video: MediaVideoResponse) =>
        this.mediaAdapter.parseVideo(video, context, slug)
      ),

      contacts: (data.press || []).map((contact: ContactPersonResponse) => this.contactPersonAdapter.parse(contact)),
      audioCount: data.audio_count,
      videoCount: data.video_count,
      imageCount: data.image_count,
      documentCount: data.document_count,
      technicalDataCount: data.technical_data_count,

      topicId: data.topic_tree?.drupal_internal__tid?.toString(),
    };
  }
}
